// Tutorial.js
// Interactive tutorial system for new users
// Provides guided tour of FlowTutor features

import React, { useEffect } from 'react';
import introJs from 'intro.js';
import 'intro.js/introjs.css';

export const startTutorial = (darkMode = false) => {
  const intro = introJs();

  // Apply dark mode styling if enabled
  if (darkMode) {
    document.documentElement.classList.add('introjs-dark');
  }

  intro.setOptions({
    steps: [
      {
        title: '👋 Welcome to NalemStudyCircle!',
        intro: `
          <div style="text-align: center; padding: 20px 10px;">
            <h3 style="margin-bottom: 16px; font-size: 26px;">Your AI-Powered Learning Companion</h3>
            <p style="font-size: 16px; margin: 12px 0;">Let's take a quick tour to show you all the amazing features!</p>
            <p style="margin-top: 16px; font-size: 14px; opacity: 0.9;">⏱️ This will only take 2 minutes</p>
          </div>
        `
      },
      {
        element: '[data-tutorial="learning-goal-input"]',
        intro: `
          <h4>🎯 Create Learning Goals - Step 1</h4>
          <p>Type any topic you want to learn in this text area.</p>
          <p>For example: "Python Programming", "SAT Math", or "Spanish"</p>
        `,
        position: 'bottom'
      },
      {
        element: '[data-tutorial="create-learning-goal-button"]',
        intro: `
          <h4>🎯 Create Learning Goals - Step 2</h4>
          <p>Click this button to plan your study schedule!</p>
          <p>Our AI will generate a personalized learning roadmap with daily lessons.</p>
        `,
        position: 'top'
      },
      {
        element: '[data-tutorial="upload-notes"]',
        intro: `
          <h4>📝 Upload Notes</h4>
          <p>Upload your .txt or .pdf notes, and we will:</p>
          <ul style="text-align: left; margin-top: 8px;">
            <li>✅ Extract key vocabulary</li>
            <li>✅ Generate flashcards</li>
            <li>✅ Create a study guide</li>
          </ul>
        `,
        position: 'bottom'
      },
      {
        element: '[data-tutorial="youtube-upload"]',
        intro: `
          <h4>🎥 Learn from YouTube Videos</h4>
          <p>Paste any educational YouTube video link!</p>
          <p>We'll analyze the content and generate flashcards or use our Study Companion feature for interactive learning.</p>
        `,
        position: 'auto'
      },
      {
        element: '[data-tutorial="discover-feed"]',
        intro: `
          <h4>🌟 Discover Learning Content</h4>
          <p>Browse and share educational content with other students!</p>
          <ul style="text-align: left; margin-top: 8px;">
            <li>View notes, quizzes, and study guides from peers</li>
            <li>Like and save helpful content</li>
            <li>Upload your own study materials</li>
            <li>Learn together as a community</li>
          </ul>
        `,
        position: 'auto'
      },
      {
        element: '[data-tutorial="ai-tutor"]',
        intro: `
          <h4>🤖 AI Tutor Chat</h4>
          <p>Stuck on a lesson? Ask questions anytime!</p>
          <p>Get instant help with explanations, examples, and guidance.</p>
          <p style="margin-top: 8px; font-size: 13px; opacity: 0.9;">💡 You have 10 free questions per session</p>
        `,
        position: 'auto'
      },
      {
        element: '[data-tutorial="vocabulary"]',
        intro: `
          <h4>📚 Vocabulary Builder</h4>
          <p>Build your vocabulary with:</p>
          <ul style="text-align: left; margin-top: 8px;">
            <li>Interactive flashcards</li>
            <li>Custom word lists</li>
            <li>Practice quizzes</li>
            <li>Multi-language support</li>
          </ul>
        `,
        position: 'auto'
      },
      {
        element: '[data-tutorial="settings"]',
        intro: `
          <h4>⚙️ Settings & Customization</h4>
          <p>Personalize your experience:</p>
          <ul style="text-align: left; margin-top: 8px;">
            <li>Dark/Light mode</li>
            <li>Language preferences</li>
            <li>Study reminders</li>
            <li>Learning goals</li>
            <li>Restart this tutorial</li>
          </ul>
        `,
        position: 'auto'
      },
      {
        title: '🎉 You\'re All Set!',
        intro: `
          <div style="text-align: center; padding: 10px;">
            <h3 style="margin-bottom: 10px;">Ready to Start Learning?</h3>
            <p>Create your first learning goal or upload some notes to get started!</p>
            <p style="margin-top: 15px; font-size: 14px; opacity: 0.8;">💡 Tip: You can restart this tutorial anytime from Settings</p>
          </div>
        `
      }
    ],
    showProgress: true,
    showBullets: true,
    exitOnOverlayClick: false,
    exitOnEsc: true,
    nextLabel: 'Next →',
    prevLabel: '← Back',
    doneLabel: 'Start Learning! 🚀',
    skipLabel: 'Skip Tour',
    disableInteraction: false,
    scrollToElement: true,
    scrollPadding: 80,
    tooltipPosition: 'auto',
    positionPrecedence: ['bottom', 'top', 'right', 'left'],
    overlayOpacity: 0.8,
    helperElementPadding: 10,
    tooltipClass: 'customTooltip',
    highlightClass: 'customHighlight'
  });

  // Event listeners for better positioning
  intro.onbeforechange(function(targetElement) {
    if (targetElement) {
      // Scroll the element into view with extra padding
      setTimeout(() => {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center'
        });
      }, 100);
    }
  });

  intro.onafterchange(function(targetElement) {
    if (targetElement) {
      // Add a custom highlight class
      targetElement.classList.add('tutorial-highlight');

      // Remove it after a short delay
      setTimeout(() => {
        targetElement.classList.remove('tutorial-highlight');
      }, 3000);
    }
  });

  // Start the tour
  intro.start();

  // Event listeners
  intro.oncomplete(() => {
    // Mark tutorial as completed in localStorage
    localStorage.setItem('flowtutor_tutorial_completed', 'true');
    if (darkMode) {
      document.documentElement.classList.remove('introjs-dark');
    }
  });

  intro.onexit(() => {
    // Mark tutorial as skipped
    localStorage.setItem('flowtutor_tutorial_completed', 'skipped');
    if (darkMode) {
      document.documentElement.classList.remove('introjs-dark');
    }
  });

  return intro;
};

// Check if user is new and should see tutorial
export const shouldShowTutorial = () => {
  const tutorialStatus = localStorage.getItem('flowtutor_tutorial_completed');
  const hasUserData = localStorage.getItem('flowtutor_current_user');

  // Show tutorial if:
  // 1. Never completed before AND
  // 2. User has logged in (has user data)
  return !tutorialStatus && hasUserData;
};

// Force restart tutorial (from settings)
export const restartTutorial = (darkMode = false) => {
  localStorage.removeItem('flowtutor_tutorial_completed');
  startTutorial(darkMode);
};

// Tutorial component wrapper
const Tutorial = ({ darkMode, autoStart = false }) => {
  useEffect(() => {
    if (autoStart && shouldShowTutorial()) {
      // Longer delay to ensure all DOM elements are loaded
      setTimeout(() => {
        // Check if key elements exist before starting
        const keyElements = [
          '[data-tutorial="learning-goal-input"]',
          '[data-tutorial="create-learning-goal-button"]',
          '[data-tutorial="upload-notes"]',
          '[data-tutorial="youtube-upload"]'
        ];

        const allElementsExist = keyElements.every(selector =>
          document.querySelector(selector) !== null
        );

        if (allElementsExist) {
          startTutorial(darkMode);
        } else {
          // Retry after another second if elements not ready
          setTimeout(() => {
            startTutorial(darkMode);
          }, 1500);
        }
      }, 1500);
    }
  }, [autoStart, darkMode]);

  return null; // This component doesn't render anything
};

export default Tutorial;
